import * as React from 'react';
import React__default, { forwardRef, useRef, useState, useImperativeHandle, useEffect } from 'react';
import { classNames, analyticsTracker } from '@egds/react-core/utils';
import { EGDSButtonSize, EGDSButton, EGDSPrimaryButton, EGDSOverlayButton } from '@egds/react-core/button';
import styles from '../styles/egds-sponsored-content-button-custom-extra-small-with-icon.css';
import { EGDSFigure } from '@egds/react-core/images';
import { EGDSLayoutPosition } from '@egds/react-core/layout-position';
import { useLocalization } from '@shared-ui/localization-context';
import { EGDSIcon, EGDSIconSize } from '@egds/react-core/icons';
import { EGDSLayoutFlexItem, EGDSLayoutFlex } from '@egds/react-core/layout-flex';
import { EGDSLoader } from '@egds/react-core/loader';
import { EGDSScrim, EGDSScrimType } from '@egds/react-core/scrim';
import styles$1 from '../styles/egds-sponsored-content-video-custom.css';

const getClassNameList$2 = classNames.bind(styles);
class ButtonCustomExtraSmallWithIcon extends React.Component {
    static displayName = 'ButtonCustomExtraSmallWithIcon';
    static defaultProps = {
        size: EGDSButtonSize.SMALL,
    };
    render() {
        const { props } = this;
        const cssClassNames = classNames(getClassNameList$2(props.size === EGDSButtonSize.LARGE ? 'button-custom-large-with-icon' : 'button-custom-extra-small-with-icon', props.className));
        return React.createElement(EGDSButton, { ...props, className: cssClassNames });
    }
}

const getClassNameList$1 = classNames.bind(styles$1);
const LoadingSpinner = () => (React__default.createElement(EGDSLayoutPosition, { type: "absolute", position: { top: 'center', left: 'center', zindex: 'layer3' } },
    React__default.createElement(EGDSLoader, { isDarkBackground: true })));
const Footer = ({ l10n, callToActionProps, controlBarProps, captionsSrc, isVideoPaused, isMuted, isCaptionsOn, isFullscreen, handleControlClick, handleMuteClick, handleClosedCaptionsClick, handleFullscreenClick, }) => {
    const { useScrim = true, useDefaultCTAButton = true, hasPlayButton = true, hasMuteButton = true, hasClosedCaptionsButton = true, hasFullscreenButton = true, } = controlBarProps || {};
    const { ctaText, ctaLink, openLinkInNewTab = false } = callToActionProps || {};
    const callToActionButton = useDefaultCTAButton && callToActionProps && (React__default.createElement(EGDSLayoutPosition, { type: "absolute", position: { zindex: 'layer3', bottom: 'three', left: 'center' } }, useScrim ? (React__default.createElement(EGDSPrimaryButton, { size: EGDSButtonSize.MEDIUM, "aria-label": ctaText, tag: "a", href: ctaLink, target: openLinkInNewTab ? '_blank' : '_self' }, ctaText)) : (React__default.createElement(EGDSOverlayButton, { size: EGDSButtonSize.LARGE, "aria-label": ctaText, tag: "a", href: ctaLink, target: openLinkInNewTab ? '_blank' : '_self' }, ctaText))));
    const buttonClassname = getClassNameList$1('sponsored-content-video-button');
    const playButtonAriaLabel = isVideoPaused
        ? l10n.formatText('video.playButton.label')
        : l10n.formatText('video.pauseButton.label');
    const playButton = hasPlayButton && (React__default.createElement(EGDSLayoutFlexItem, null, useScrim ? (React__default.createElement("button", { id: "control-button", type: "button", "aria-label": playButtonAriaLabel, onClick: handleControlClick, className: buttonClassname },
        React__default.createElement(EGDSIcon, { name: isVideoPaused ? 'play_arrow' : 'pause', theme: "inverse", size: EGDSIconSize.MEDIUM }))) : (React__default.createElement(EGDSOverlayButton, { size: EGDSButtonSize.LARGE, id: "control-button", "aria-label": playButtonAriaLabel, role: "button", onClick: handleControlClick },
        React__default.createElement(EGDSIcon, { name: isVideoPaused ? 'play_arrow' : 'pause', size: EGDSIconSize.LARGE })))));
    const muteButtonAriaLabel = isMuted
        ? l10n.formatText('videoCustom.volume.unmute.label')
        : l10n.formatText('videoCustom.volume.mute.label');
    const muteButton = hasMuteButton && (React__default.createElement(EGDSLayoutFlexItem, null, useScrim ? (React__default.createElement("button", { id: "mute-button", type: "button", "aria-label": muteButtonAriaLabel, onClick: handleMuteClick, className: buttonClassname },
        React__default.createElement(EGDSIcon, { name: isMuted ? 'volume_off' : 'volume_up', theme: "inverse", size: EGDSIconSize.MEDIUM }))) : (React__default.createElement(EGDSOverlayButton, { id: "mute-button", size: EGDSButtonSize.LARGE, "aria-label": muteButtonAriaLabel, role: "button", onClick: handleMuteClick },
        React__default.createElement(EGDSIcon, { name: isMuted ? 'volume_off' : 'volume_up' })))));
    const closedCaptionAriaLabel = captionsSrc
        ? isCaptionsOn
            ? l10n.formatText('videoCustom.closedCaptions.off.label')
            : l10n.formatText('videoCustom.closedCaptions.on.label')
        : l10n.formatText('videoCustom.closedCaptions.notAvailable.label');
    const closedCaptionsButtonIcon = captionsSrc
        ? isCaptionsOn
            ? 'closed_caption'
            : 'closed_caption_off'
        : 'closed_caption_disabled';
    const closedCaptionButton = hasClosedCaptionsButton && (React__default.createElement(EGDSLayoutFlexItem, null, useScrim ? (React__default.createElement("button", { id: "closed-caption-button", type: "button", "aria-label": closedCaptionAriaLabel, onClick: handleClosedCaptionsClick, className: buttonClassname, disabled: !captionsSrc },
        React__default.createElement(EGDSIcon, { name: closedCaptionsButtonIcon, theme: "inverse", size: EGDSIconSize.MEDIUM }))) : (React__default.createElement(EGDSOverlayButton, { id: "closed-caption-button", size: EGDSButtonSize.LARGE, "aria-label": closedCaptionAriaLabel, role: "button", onClick: handleClosedCaptionsClick, disabled: !captionsSrc },
        React__default.createElement(EGDSIcon, { name: closedCaptionsButtonIcon })))));
    const fullscreenButtonAriaLabel = isFullscreen
        ? l10n.formatText('videoCustom.fullscreen.off.label')
        : l10n.formatText('videoCustom.fullscreen.on.label');
    const fullscreenButton = hasFullscreenButton && (React__default.createElement(EGDSLayoutFlexItem, null, useScrim ? (React__default.createElement("button", { id: "fullscreen-button", type: "button", "aria-label": fullscreenButtonAriaLabel, onClick: handleFullscreenClick, className: buttonClassname },
        React__default.createElement(EGDSIcon, { name: isFullscreen ? 'collapse' : 'expand', theme: "inverse", size: EGDSIconSize.MEDIUM }))) : (React__default.createElement(EGDSOverlayButton, { id: "fullscreen-button", size: EGDSButtonSize.LARGE, "aria-label": fullscreenButtonAriaLabel, role: "button", onClick: handleFullscreenClick },
        React__default.createElement(EGDSIcon, { name: isFullscreen ? 'collapse' : 'expand' })))));
    return (React__default.createElement(React__default.Fragment, null,
        callToActionButton,
        useScrim ? (React__default.createElement(EGDSScrim, { type: EGDSScrimType.BOTTOM },
            React__default.createElement(EGDSLayoutFlex, { direction: "row-reverse", space: "three" },
                fullscreenButton,
                closedCaptionButton,
                muteButton,
                playButton))) : (React__default.createElement(EGDSLayoutPosition, { type: "absolute", position: { zindex: 'layer3', bottom: 'three', right: 'three' } },
            React__default.createElement(EGDSLayoutFlex, { direction: "row", space: "three" },
                playButton,
                muteButton,
                closedCaptionButton,
                fullscreenButton)))));
};

var VideoCustomAspectRatio;
(function (VideoCustomAspectRatio) {
    VideoCustomAspectRatio["R4_3"] = "ratio-4-3";
    VideoCustomAspectRatio["R16_9"] = "ratio-16-9";
    VideoCustomAspectRatio["R21_9"] = "ratio-21-9";
    VideoCustomAspectRatio["R3_4"] = "ratio-3-4";
    VideoCustomAspectRatio["R9_16"] = "ratio-9-16";
})(VideoCustomAspectRatio || (VideoCustomAspectRatio = {}));
var VideoCustomClickAction;
(function (VideoCustomClickAction) {
    VideoCustomClickAction["PLAY_PAUSE"] = "play-pause";
    VideoCustomClickAction["CALL_TO_ACTION"] = "call-to-action";
    VideoCustomClickAction["NONE"] = "none";
})(VideoCustomClickAction || (VideoCustomClickAction = {}));

const getClassNameList = classNames.bind(styles$1);
const VideoCustom = forwardRef((props, ref) => {
    const l10n = useLocalization();
    const { videoResource: { src, thumbnail, captionsSrc }, adBadge, analytics, autoplay = true, preloadAuto = true, callToActionProps, footerContent, midSection, isMobile = false, onComplete, onError, preserveAspectRatio = false, ratio = VideoCustomAspectRatio.R16_9, topSection, videoAriaLabel = l10n.formatText('videoCustom.video.label'), videoClickAction = VideoCustomClickAction.PLAY_PAUSE, visibleAutoplayThreshold = 0.5, isVideoMuted = true, videoId = "video-player" } = props;
    const { customFooter, controlBarProps } = footerContent || {};
    const { controlButtonsInactivityTimeout = 3 } = controlBarProps || {};
    const { ctaLink, openLinkInNewTab = false, ctaClickAnalytics } = callToActionProps || {};
    // Standard for video ads is to count as a view after 2 seconds.
    const viewDurationTrackingThreshold = 2;
    const videoAnalytics = analytics || {};
    const { viewAnalytics, firstQuartileAnalytics, midpointAnalytics, thirdQuartileAnalytics, completeAnalytics, playAnalytics, pauseAnalytics, muteAnalytics, unmuteAnalytics, bufferingAnalytics, } = videoAnalytics;
    const videoElement = useRef(null);
    const [isVideoPaused, setIsVideoPaused] = useState(!autoplay);
    const [isMuted, setIsMuted] = useState(isVideoMuted);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isCaptionsOn, setIsCaptionsOn] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [isBuffering, setIsBuffering] = useState(autoplay);
    const [isLooping, setIsLooping] = useState(false);
    const handleMuteClick = () => {
        if (videoElement.current instanceof HTMLVideoElement) {
            if (videoElement.current.muted) {
                unmuteAnalytics && analyticsTracker(unmuteAnalytics);
            }
            else {
                muteAnalytics && analyticsTracker(muteAnalytics);
            }
            videoElement.current.muted = !videoElement.current.muted;
            setIsMuted(!isMuted);
        }
    };
    const handleControlClick = () => {
        if (videoElement.current instanceof HTMLVideoElement) {
            if (videoElement.current.paused) {
                setIsVideoPaused(false);
                playAnalytics && analyticsTracker(playAnalytics);
            }
            else {
                setIsVideoPaused(true);
                pauseAnalytics && analyticsTracker(pauseAnalytics);
            }
        }
    };
    // To be called when an overlay component needs to be shown on top of the video
    // Pause if it's currently playing
    const showOverlay = () => {
        if (videoElement.current instanceof HTMLVideoElement && !isVideoPaused) {
            videoElement.current.pause();
        }
    };
    // To be called when an overlay component is dismissed
    // Resume if it was playing before
    const dismissOverlay = () => {
        if (videoElement.current instanceof HTMLVideoElement && !isVideoPaused) {
            videoElement.current.play();
        }
    };
    // To be called to toggle Play and Pause
    const togglePlayPause = () => {
        if (videoElement.current instanceof HTMLVideoElement) {
            const isPaused = videoElement.current.paused;
            if (isPaused) {
                videoElement.current.play();
            }
            else {
                videoElement.current.pause();
            }
            setIsVideoPaused(!isPaused);
        }
    };
    // To be called to toggle Mute and Unmute
    const toggleMuteUnmute = () => {
        if (videoElement.current instanceof HTMLVideoElement) {
            videoElement.current.muted = !videoElement.current.muted;
            setIsMuted(videoElement.current.muted);
        }
    };
    useImperativeHandle(ref, () => ({
        showOverlay,
        dismissOverlay,
        togglePlayPause,
        toggleMuteUnmute,
    }));
    const handleClosedCaptionsClick = () => {
        if (videoElement.current instanceof HTMLVideoElement) {
            const ccTrack = Array.from(videoElement.current.textTracks).find((track) => track.kind === 'captions');
            if (ccTrack) {
                if (isCaptionsOn) {
                    ccTrack.mode = 'disabled';
                }
                else {
                    ccTrack.mode = 'showing';
                }
                setIsCaptionsOn(!isCaptionsOn);
            }
        }
    };
    const handleFullscreenClick = () => {
        const videoContainer = videoElement.current?.parentElement;
        if (isFullscreen) {
            document.exitFullscreen();
        }
        else if (videoContainer instanceof HTMLElement) {
            videoContainer.requestFullscreen();
        }
        setIsFullscreen(!isFullscreen);
    };
    const handleVideoComplete = () => {
        if (videoElement.current instanceof HTMLVideoElement) {
            completeAnalytics && analyticsTracker(completeAnalytics);
            onComplete && onComplete();
            setIsLooping(true);
            setIsBuffering(false);
            videoElement.current.play();
        }
    };
    const handleLoadError = (event) => {
        onError && onError(event);
    };
    const [isTabVisible, setIsTabVisible] = useState(true);
    const handleVisibilityChange = () => {
        setIsTabVisible(document.visibilityState === 'visible');
    };
    const isMounted = useRef(false);
    useEffect(() => {
        isMounted.current = true;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry && isMounted.current) {
                setIsVisible(entry.isIntersecting);
            }
        }, { threshold: visibleAutoplayThreshold });
        if (videoElement.current) {
            observer.observe(videoElement.current);
        }
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            isMounted.current = false;
            observer.disconnect();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);
    // Show controls ref set to true at first so reappearance tracking event is not sent the first time the controls appear on video play button clicked.
    const showControlsRef = useRef(true);
    const [recentlyResumed, setRecentlyResumed] = useState(true);
    const [showControls, setShowControls] = useState(false);
    let controlsTimeoutId;
    const showControlsHandler = (show) => {
        setShowControls(show);
        showControlsRef.current = show;
    };
    const handleControlsTimeout = () => {
        if (showControlsRef.current && !videoElement.current?.paused) {
            showControlsHandler(false);
        }
        setRecentlyResumed(false);
    };
    const resetInactivityTimer = () => {
        clearTimeout(controlsTimeoutId);
        controlsTimeoutId = setTimeout(handleControlsTimeout, controlButtonsInactivityTimeout * 1000);
    };
    const handleMouseMove = () => {
        if (!showControlsRef.current) {
            showControlsHandler(true);
        }
        resetInactivityTimer();
    };
    useEffect(() => {
        if (videoElement.current instanceof HTMLVideoElement && !isVideoPaused) {
            if (!isMobile) {
                videoElement.current.addEventListener('mousemove', handleMouseMove);
            }
            setRecentlyResumed(true);
            resetInactivityTimer();
        }
        return () => {
            clearTimeout(controlsTimeoutId);
            if (!isMobile) {
                videoElement.current?.removeEventListener('mousemove', handleMouseMove);
            }
        };
    }, [isVideoPaused]);
    useEffect(() => {
        // Only override showControls if video not recently resumed - desktop only
        if (!isVideoPaused && !recentlyResumed && !isMobile) {
            showControlsHandler(isHovering);
            if (isHovering) {
                resetInactivityTimer();
            }
        }
        return () => {
            clearTimeout(controlsTimeoutId);
        };
    }, [isHovering, recentlyResumed]);
    useEffect(() => {
        if (videoElement.current instanceof HTMLVideoElement && isVisible && isTabVisible) {
            if (isVideoPaused) {
                videoElement.current.pause();
            }
            else {
                videoElement.current.play();
                setIsVideoPaused(false);
            }
            videoElement.current.muted = isMuted;
            showControlsHandler(true);
            resetInactivityTimer();
        }
        else if (videoElement.current instanceof HTMLVideoElement) {
            videoElement.current.pause();
        }
    }, [isVisible, isTabVisible, isVideoPaused, isMuted, visibleAutoplayThreshold]);
    const [videoProgressTrackers, setVideoProgressTrackers] = useState({
        view: false,
        firstQuartile: false,
        midpoint: false,
        thirdQuartile: false,
        viewedPreview: false,
    });
    const handleTimeProgress = () => {
        if (videoElement.current instanceof HTMLVideoElement && videoElement.current.duration !== 0) {
            const { currentTime } = videoElement.current;
            const { duration } = videoElement.current;
            const viewed = currentTime >= viewDurationTrackingThreshold;
            const firstQuartileComplete = currentTime / duration >= 0.25;
            const midpointComplete = currentTime / duration >= 0.5;
            const thirdQuartileComplete = currentTime / duration >= 0.75;
            if (viewed && !videoProgressTrackers.view) {
                viewAnalytics && analyticsTracker(viewAnalytics);
                setVideoProgressTrackers({ ...videoProgressTrackers, view: true });
            }
            if (firstQuartileComplete && !videoProgressTrackers.firstQuartile) {
                firstQuartileAnalytics && analyticsTracker(firstQuartileAnalytics);
                setVideoProgressTrackers({ ...videoProgressTrackers, firstQuartile: true });
            }
            if (midpointComplete && !videoProgressTrackers.midpoint) {
                midpointAnalytics && analyticsTracker(midpointAnalytics);
                setVideoProgressTrackers({ ...videoProgressTrackers, midpoint: true });
            }
            if (thirdQuartileComplete && !videoProgressTrackers.thirdQuartile) {
                thirdQuartileAnalytics && analyticsTracker(thirdQuartileAnalytics);
                setVideoProgressTrackers({ ...videoProgressTrackers, thirdQuartile: true });
            }
        }
    };
    const handleVideoClick = () => {
        if (isMobile && !showControls) {
            // First tap on a mobile device when the controls are hidden will unhide the controls
            handleMouseMove();
        }
        else {
            switch (videoClickAction) {
                case VideoCustomClickAction.CALL_TO_ACTION:
                    ctaClickAnalytics && analyticsTracker(ctaClickAnalytics);
                    window.open(ctaLink, openLinkInNewTab ? '_blank' : '_self');
                    break;
                case VideoCustomClickAction.NONE:
                    break;
                // Play/pause is the default action
                case VideoCustomClickAction.PLAY_PAUSE:
                default:
                    handleControlClick();
                    break;
            }
        }
    };
    const handleVideoBuffering = () => {
        if (!isLooping) {
            bufferingAnalytics && analyticsTracker(bufferingAnalytics);
            setIsBuffering(true);
            if (props?.onVideoLoading) {
                props.onVideoLoading(true);
            }
        }
    };
    const handleVideoPlaying = () => {
        setIsBuffering(false);
        setIsLooping(false);
        if (props?.onVideoLoading) {
            props.onVideoLoading(false);
        }
    };
    const videoComponent = (React__default.createElement("video", { "aria-label": videoAriaLabel, controlsList: "nodownload noplaybackrate", disablePictureInPicture: true, id: videoId, muted: true, onEnded: handleVideoComplete, onError: handleLoadError, onTimeUpdate: handleTimeProgress, onClick: handleVideoClick, onWaiting: handleVideoBuffering, onPlaying: handleVideoPlaying, playsInline: true, poster: thumbnail, preload: preloadAuto ? 'auto' : 'metadata', ref: videoElement, className: getClassNameList('sponsored-content-video', {
            'sponsored-content-video-preserve-aspect-ratio': preserveAspectRatio,
        }) },
        React__default.createElement("source", { src: src }),
        captionsSrc && React__default.createElement("track", { src: captionsSrc, kind: "captions", srcLang: "en", label: "English" })));
    return (React__default.createElement("div", { onMouseEnter: () => setIsHovering(true), onMouseLeave: () => setIsHovering(false) },
        React__default.createElement(EGDSFigure, { className: getClassNameList(`sponsored-content-video-${ratio}`) },
            videoComponent,
            isBuffering && React__default.createElement(LoadingSpinner, null),
            showControls &&
                (customFooter ? (React__default.createElement(EGDSLayoutPosition, { type: "absolute", position: { zindex: 'layer3', bottom: 'three', left: 'center' } }, customFooter)) : (React__default.createElement(Footer, { l10n: l10n, controlBarProps: controlBarProps, captionsSrc: captionsSrc, isVideoPaused: isVideoPaused, isMuted: isMuted, isCaptionsOn: isCaptionsOn, isFullscreen: isFullscreen, handleControlClick: handleControlClick, handleMuteClick: handleMuteClick, handleClosedCaptionsClick: handleClosedCaptionsClick, handleFullscreenClick: handleFullscreenClick }))),
            topSection && (showControls || !topSection.disappearsWithControls) && (React__default.createElement(EGDSLayoutPosition, { type: "absolute", position: { zindex: 'layer3', top: 'three', left: 'center' } }, topSection.sectionComponent)),
            midSection && (showControls || !midSection.disappearsWithControls) && (React__default.createElement(EGDSLayoutPosition, { type: "absolute", position: { zindex: 'layer3', top: 'center', left: 'center' } }, midSection.sectionComponent)),
            React__default.createElement(EGDSLayoutPosition, { type: "absolute", position: { zindex: 'layer3', bottom: 'three', left: 'three' } }, adBadge))));
});
VideoCustom.displayName = 'VideoCustom';

export { ButtonCustomExtraSmallWithIcon, VideoCustom, VideoCustomAspectRatio, VideoCustomClickAction };
//# sourceMappingURL=index.esm.js.map
